(function (root) {
  'use strict';
  function reconcileClock(saved, cart, now, duration, scope) {
    const token = String(cart.token || '');
    const count = Number(cart.item_count || 0);
    const valid = saved && saved.version === 1 && saved.scope === scope && saved.token === token && Number.isFinite(saved.expiresAt);
    let state;
    if (!count) state = { version: 1, scope, token, expiresAt: 0, empty: true };
    else if (!valid || saved.empty || !saved.expiresAt) state = { version: 1, scope, token, expiresAt: now + duration * 1000, empty: false };
    else state = { ...saved, empty: false };
    return { state, expired: count > 0 && state.expiresAt <= now };
  }
  function protectionPlan(cart, variantId, mainProductId, wanted) {
    const items = cart.items || [];
    const lines = items.filter(item => String(item.variant_id || item.id) === String(variantId));
    const eligible = items.some(item => String(item.product_id) === String(mainProductId));
    const selected = eligible && (wanted === undefined ? lines.length > 0 : wanted);
    const updates = {};
    lines.forEach((item, index) => {
      const quantity = selected && index === 0 ? 1 : 0;
      if (Number(item.quantity) !== quantity) updates[item.key] = quantity;
    });
    return { eligible, selected, add: selected && lines.length === 0, updates };
  }
  function remainingTime(expiresAt, now) {
    const seconds = Math.max(0, Math.ceil((expiresAt - now) / 1000));
    return String(Math.floor(seconds / 60)).padStart(2, '0') + ':' + String(seconds % 60).padStart(2, '0');
  }
  const core = { reconcileClock, protectionPlan, remainingTime };
  if (typeof module !== 'undefined' && module.exports) module.exports = core;
  if (!root.document || root.HqCartControlsInstalled) return;
  root.HqCartControlsInstalled = true;
  const originalFetch = root.fetch.bind(root);
  const routeRoot = (root.Shopify && root.Shopify.routes && root.Shopify.routes.root) || '/';
  const url = suffix => routeRoot + suffix;
  const safeJson = value => { try { return JSON.parse(value); } catch (_) { return null; } };
  class CartControlsManager {
    constructor() {
      this.nodes = new Set();
      this.cart = null;
      this.state = null;
      this.busy = false;
      this.expiring = false;
      this.error = false;
      this.started = false;
      this.queued = null;
      this.memory = null;
    }
    register(node) {
      this.nodes.add(node);
      this.config = {
        scope: node.dataset.scope,
        duration: Math.max(60, Number(node.dataset.duration) || 300),
        timer: node.dataset.timer === 'true',
        protection: node.dataset.protection === 'true',
        variant: node.dataset.variant,
        mainProduct: node.dataset.mainProduct
      };
      this.key = 'hq-cart-controls:' + root.location.host + ':' + this.config.scope;
      if (!this.started) this.start();
      this.render();
      this.queueRefresh();
    }
    storageRead() {
      for (const name of ['localStorage', 'sessionStorage']) {
        try {
          const value = root[name].getItem(this.key);
          if (value) return safeJson(value);
        } catch (_) {}
      }
      return this.memory;
    }
    storageWrite(state) {
      this.memory = state;
      const value = JSON.stringify(state);
      for (const name of ['localStorage', 'sessionStorage']) {
        try { root[name].setItem(this.key, value); } catch (_) {}
      }
    }
    start() {
      this.started = true;
      this.timer = root.setInterval(() => this.tick(), 1000);
      this.poll = root.setInterval(() => {
        if (!root.document.hidden && this.cart && this.cart.item_count > 0) this.queueRefresh();
      }, 15000);
      root.addEventListener('pageshow', () => this.queueRefresh());
      root.addEventListener('focus', () => this.queueRefresh());
      root.document.addEventListener('visibilitychange', () => {
        if (!root.document.hidden) this.queueRefresh();
      });
      root.addEventListener('storage', event => {
        if (event.key === this.key) {
          this.state = this.storageRead();
          this.queueRefresh();
        }
      });
      for (const event of ['cart:updated', 'cart:refresh', 'cart:change', 'kaching:cart-updated']) {
        root.document.addEventListener(event, () => this.queueRefresh());
      }
      root.fetch = (...args) => {
        const result = originalFetch(...args);
        let address;
        try { address = new URL(typeof args[0] === 'string' ? args[0] : args[0].url, root.location.href); } catch (_) {}
        if (address && address.origin === root.location.origin && /\/cart\/(add|change|update|clear)(\.js)?\/?$/.test(address.pathname)) {
          result.then(response => {
            if (!response.ok) return;
            response.clone().json().then(cart => {
              if (cart.token && typeof cart.item_count === 'number') this.acceptCart(cart);
              this.queueRefresh();
            }).catch(() => this.queueRefresh());
          }).catch(() => {});
        }
        return result;
      };
    }
    queueRefresh() {
      root.clearTimeout(this.queued);
      this.queued = root.setTimeout(() => this.refresh(), 100);
    }
    async request(suffix, body) {
      const options = body === undefined
        ? { credentials: 'same-origin', cache: 'no-store' }
        : { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body) };
      const response = await originalFetch(url(suffix), options);
      const data = await response.json();
      if (!response.ok || data.status >= 400) throw new Error('Cart request failed');
      return data;
    }
    acceptCart(cart) {
      if (!cart || !cart.token || typeof cart.item_count !== 'number') return;
      this.cart = cart;
      const result = reconcileClock(this.storageRead(), cart, Date.now(), this.config.duration, this.config.scope);
      this.state = result.state;
      this.storageWrite(this.state);
      this.render();
    }
    async refresh() {
      if (this.refreshing || this.busy || this.expiring) return;
      this.refreshing = true;
      try {
        this.acceptCart(await this.request('cart.js'));
        this.error = false;
        if (this.config.timer && this.cart.item_count && this.state.expiresAt <= Date.now()) {
          this.refreshing = false;
          await this.expire();
          return;
        }
        if (this.config.protection && this.config.variant) {
          const plan = protectionPlan(this.cart, this.config.variant, this.config.mainProduct);
          if (Object.keys(plan.updates).length) {
            this.busy = true;
            await this.mutate('cart/update.js', { updates: plan.updates });
            this.busy = false;
          }
        }
      } catch (_) { this.error = true; }
      finally { this.refreshing = false; this.busy = false; this.render(); }
    }
    tick() {
      this.render();
      if (this.config && this.config.timer && this.cart && this.cart.item_count && this.state && this.state.expiresAt <= Date.now()) this.expire();
    }
    async expire() {
      if (this.expiring || this.busy || !this.config.timer) return;
      this.expiring = true;
      try {
        const cart = await this.request('cart.js');
        const saved = this.storageRead();
        const result = reconcileClock(saved, cart, Date.now(), this.config.duration, this.config.scope);
        this.acceptCart(cart);
        if (!result.expired) return;
        const data = await this.request('cart/clear.js', { sections: ['cart-drawer', 'cart-icon-bubble'], sections_url: root.location.pathname });
        this.acceptCart(data);
        this.renderCart(data);
        this.error = false;
      } catch (_) { this.error = true; }
      finally { this.expiring = false; this.render(); }
    }
    async mutate(suffix, body) {
      const data = await this.request(suffix, { ...body, sections: ['cart-drawer', 'cart-icon-bubble'], sections_url: root.location.pathname });
      const cart = data.token && typeof data.item_count === 'number' ? data : await this.request('cart.js');
      this.acceptCart(cart);
      this.renderCart({ ...cart, sections: data.sections });
    }
    async setProtection(wanted) {
      if (this.busy || this.expiring || !this.config.protection) { this.render(); return; }
      this.busy = true;
      this.error = false;
      this.render();
      try {
        this.acceptCart(await this.request('cart.js'));
        if (this.config.timer && this.cart.item_count && this.state.expiresAt <= Date.now()) {
          this.busy = false;
          await this.expire();
          return;
        }
        const plan = protectionPlan(this.cart, this.config.variant, this.config.mainProduct, wanted);
        if (plan.add) await this.mutate('cart/add.js', { items: [{ id: Number(this.config.variant), quantity: 1, properties: { '_hq_cart_addon': 'damaged-shipment' } }] });
        else if (Object.keys(plan.updates).length) await this.mutate('cart/update.js', { updates: plan.updates });
      } catch (_) { this.error = true; }
      finally { this.busy = false; this.render(); this.queueRefresh(); }
    }
    renderCart(data) {
      if (!data.sections) return;
      const drawer = root.document.querySelector('cart-drawer');
      const wasOpen = drawer && drawer.classList.contains('active');
      if (drawer && wasOpen && typeof drawer.renderContents === 'function') {
        drawer.renderContents(data);
      } else {
        const html = data.sections['cart-drawer'];
        if (drawer && html) {
          const parsed = new DOMParser().parseFromString(html, 'text/html');
          const fresh = parsed.querySelector('#CartDrawer');
          const current = drawer.querySelector('#CartDrawer');
          if (fresh && current) current.innerHTML = fresh.innerHTML;
          drawer.classList.toggle('is-empty', Number(data.item_count) === 0);
          const overlay = drawer.querySelector('#CartDrawer-Overlay');
          if (overlay) overlay.addEventListener('click', () => drawer.close());
        }
        const bubbleHtml = data.sections['cart-icon-bubble'];
        if (bubbleHtml) {
          const parsed = new DOMParser().parseFromString(bubbleHtml, 'text/html');
          const fresh = parsed.querySelector('#shopify-section-cart-icon-bubble');
          const bubble = root.document.querySelector('#cart-icon-bubble');
          if (bubble) bubble.innerHTML = fresh ? fresh.innerHTML : bubbleHtml;
        }
      }
      root.document.dispatchEvent(new CustomEvent('hq:cart-updated', { detail: { cart: this.cart } }));
    }
    render() {
      for (const node of this.nodes) {
        if (!node.isConnected) { this.nodes.delete(node); continue; }
        const count = this.cart ? Number(this.cart.item_count) : 0;
        const timer = node.querySelector('[data-hq-timer]');
        const countdown = node.querySelector('[data-hq-countdown]');
        const protection = node.querySelector('[data-hq-protection]');
        const checkbox = node.querySelector('[data-hq-checkbox]');
        const error = node.querySelector('[data-hq-error]');
        if (timer) timer.hidden = !this.config.timer || !count;
        if (countdown && this.state) countdown.textContent = remainingTime(this.state.expiresAt, Date.now());
        const plan = this.cart ? protectionPlan(this.cart, this.config.variant, this.config.mainProduct) : { eligible: false, selected: false };
        if (protection) protection.hidden = !this.config.protection || !this.config.variant || !plan.eligible;
        if (checkbox) {
          checkbox.checked = Boolean(plan.selected);
          checkbox.disabled = this.busy || this.expiring || !plan.eligible;
        }
        if (error) error.hidden = !this.error;
        node.hidden = !count && !this.error;
      }
    }
  }
  const manager = new CartControlsManager();
  class HqCartControls extends HTMLElement {
    connectedCallback() {
      if (root.Shopify && root.Shopify.designMode) {
        this.hidden = false;
        this.querySelector('[data-hq-timer]').hidden = this.dataset.timer !== 'true';
        this.querySelector('[data-hq-protection]').hidden = this.dataset.protection !== 'true';
        this.querySelector('[data-hq-checkbox]').disabled = true;
        return;
      }
      if (!this.bound) {
        this.bound = true;
        this.addEventListener('change', event => {
          if (event.target.matches('[data-hq-checkbox]')) manager.setProtection(event.target.checked);
        });
      }
      manager.register(this);
    }
    disconnectedCallback() { manager.nodes.delete(this); }
  }
  if (!root.customElements.get('hq-cart-controls')) root.customElements.define('hq-cart-controls', HqCartControls);
})(typeof window === 'undefined' ? globalThis : window);
